import { useState, useEffect, useContext, createContext } from 'react';
import { 
  detectCountry, 
  detectLanguage, 
  getCurrencyForCountry, 
  loadTranslations, 
  translate,
  formatPrice 
} from '../utils/i18n';
// Import default translations to avoid loading delay
import defaultTranslations from '../locales/en-IE.json';

// Create context for internationalization
const I18nContext = createContext();

export const useI18n = () => {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used within an I18nProvider');
  }
  return context;
};

export const I18nProvider = ({ children }) => {
  const [country, setCountry] = useState('IE');
  const [language, setLanguage] = useState('en-IE');
  const [currency, setCurrency] = useState({ code: 'EUR', symbol: '€' });
  const [translations, setTranslations] = useState(defaultTranslations);
  const [isLoading, setIsLoading] = useState(false); // Start with false since we have default translations

  // Initialize internationalization
  useEffect(() => {
    const initializeI18n = async () => {
      try {
        // First try browser language detection (fast)
        const browserLang = navigator.language || navigator.languages[0];
        let initialLanguage = 'en-IE';
        
        if (browserLang) {
          const langCode = browserLang.split('-')[0].toLowerCase();
          const regionCode = browserLang.split('-')[1]?.toUpperCase();
          
          if (langCode === 'en') {
            if (regionCode === 'US' || regionCode === 'CA') {
              initialLanguage = 'en-US';
            } else {
              initialLanguage = 'en-IE';
            }
          } else if (['ga', 'fr', 'de', 'es'].includes(langCode)) {
            initialLanguage = langCode;
          }
        }

        // If we need different translations than default, show loading briefly
        if (initialLanguage !== 'en-IE') {
          setIsLoading(true);
          const loadedTranslations = await loadTranslations(initialLanguage);
          setTranslations(loadedTranslations);
          setLanguage(initialLanguage);
        }

        // Detect country in background (for currency) - no loading state
        detectCountry().then(detectedCountry => {
          setCountry(detectedCountry);
          const detectedCurrency = getCurrencyForCountry(detectedCountry);
          setCurrency(detectedCurrency);
        }).catch(error => {
          console.warn('Failed to detect country, using defaults:', error);
        });

      } catch (error) {
        console.error('Failed to initialize internationalization:', error);
        // Keep defaults that are already set
      } finally {
        setIsLoading(false);
      }
    };

    initializeI18n();
  }, []);

  // Function to change language manually
  const changeLanguage = async (newLanguage) => {
    try {
      setIsLoading(true);
      const newTranslations = await loadTranslations(newLanguage);
      setLanguage(newLanguage);
      setTranslations(newTranslations);
    } catch (error) {
      console.error('Failed to change language:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Translation function
  const t = (key, params) => translate(translations, key, params);

  // Price formatting function
  const formatLocalPrice = (price) => formatPrice(price, currency);

  const value = {
    country,
    language,
    currentLanguage: language,
    currency,
    translations,
    isLoading,
    t,
    formatLocalPrice,
    changeLanguage
  };

  return (
    <I18nContext.Provider value={value}>
      {children}
    </I18nContext.Provider>
  );
}; 